import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { ProductGeneralInfoComponent } from './product-general/product-general-info.component';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  catchError,
  EMPTY,
  finalize,
  forkJoin,
  map,
  Observable,
  of,
  switchMap,
  take,
  tap,
} from 'rxjs';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import {
  PRODUCT_BRAND_PATTERN,
  PRODUCT_DESCRIPTION_PATTERN,
  PRODUCT_NAME_PATTERN,
  PRODUCT_PRICE,
  PRODUCT_UPC_PATTERN,
} from '../../validators/inventory-form.validator';
import { IconComponent } from '@app/components/shared/icon/icon.component';
import { IconName } from '@app/utils/enums/icon-enum';
import {
  InventoryItemFormValue,
  CreateInventoryItemDto,
  InventoryItemFullInformation,
} from '@app/utils/interfaces/inventory-item.interface';
import { StatusModalComponent } from '@app/components/shared/status-modal/status-modal.component';
import { TabsComponent } from '@app/components/shared/tabs/tabs.component';
import { InventoryService } from '@app/services/inventory/inventory.service';
import { EMPTY_INVENTORY_ITEM } from '@app/utils/constants/inventory-table.constant';
import {
  InventoryModalMode,
  SECTIONS,
  InventoryItemModalSection,
} from '@app/utils/types/inventory-item-modal-section.types';
import { CategoriesService } from '@app/pages/home-page/shop-settings/settings-categories/services/categories.service';
import { BrandsService } from '@app/services/brands/brands.service';
import { mapFormToInventoryItemDto } from '@app/utils/helpers/inventory-item.mapper';
import { HttpStatusCode } from '@app/utils/enums/http-status-codes.enum';
import { ProductDetailsInfoComponent } from './product-details-info/product-details-info.component';

@Component({
  selector: 'app-inventory-item-modal',
  standalone: true,
  imports: [
    TranslatePipe,
    IconComponent,
    MatProgressSpinnerModule,
    ProductGeneralInfoComponent,
    ProductDetailsInfoComponent,
    TabsComponent,
  ],
  templateUrl: './inventory-item-modal.component.html',
  styleUrl: './inventory-item-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InventoryItemModalComponent implements OnInit {
  private readonly dialogRef = inject(MatDialogRef<InventoryItemModalComponent>);
  private readonly errDialog = inject(MatDialog);
  private readonly inventoryService = inject(InventoryService);
  private readonly categoryService = inject(CategoriesService);
  private readonly brandService = inject(BrandsService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly fb = inject(FormBuilder);

  private readonly modeHandlers: Record<InventoryModalMode, () => void> = {
    view: () => this.viewInventoryItem(this.dialogData.id!),
    create: () => this.inventoryItem.set(EMPTY_INVENTORY_ITEM),
  };

  protected isLoading = signal(false);
  protected inventoryItem = signal<InventoryItemFullInformation>(EMPTY_INVENTORY_ITEM);

  public readonly dialogData = inject<{ id?: string; mode: InventoryModalMode }>(MAT_DIALOG_DATA);
  public readonly tabs = SECTIONS;
  public readonly iconName = IconName;
  public form!: FormGroup;

  public activeTab = signal<InventoryItemModalSection>('general');

  public ngOnInit(): void {
    this.initForm();
    this.modeHandlers[this.dialogData.mode]();
  }

  public onClose(): void {
    this.dialogRef.close();
  }

  public onAdd(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const formValue = this.form.value;
    this.isLoading.set(true);

    this.ensureBrandAndCategory(formValue.productBrand, formValue.productCategory)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap(({ brandId, categoryId }) => {
          const formValue = this.form.value as InventoryItemFormValue;
          const dto = mapFormToInventoryItemDto(formValue, brandId, categoryId);
          this.submitInventoryItem(dto);
        }),
        catchError(() => {
          this.openErrorModal();
          return EMPTY;
        }),
        finalize(() => this.isLoading.set(false)),
      )
      .subscribe();
  }

  protected viewInventoryItem(id: string): void {
    this.isLoading.set(true);

    this.inventoryService
      .getInventoryItemById(id)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap((res) => {
          this.inventoryItem.set(res);
        }),
        catchError(() => {
          this.openErrorModal();
          this.onClose();
          return EMPTY;
        }),
        finalize(() => {
          this.isLoading.set(false);
        }),
      )
      .subscribe();
  }

  private initForm(): void {
    this.form = this.fb.group({
      productType: [this.inventoryItem()?.hasVariations ?? EMPTY_INVENTORY_ITEM.hasVariations],
      productName: ['', [Validators.required, Validators.pattern(PRODUCT_NAME_PATTERN)]],
      productBrand: ['', [Validators.required, Validators.pattern(PRODUCT_BRAND_PATTERN)]],
      productUpc: ['', [Validators.required, Validators.pattern(PRODUCT_UPC_PATTERN)]],
      productCategory: ['', Validators.required],
      priceUsd: ['', [Validators.required, Validators.pattern(PRODUCT_PRICE)]],
      productDescription: [
        '',
        [Validators.required, Validators.pattern(PRODUCT_DESCRIPTION_PATTERN)],
      ],
      isActive: [true]
    });
  }

  private openErrorModal(message?: string): void {
    this.errDialog.open(StatusModalComponent, {
      data: {
        isSuccess: false,
        title: 'inventory.apiErrors.serverErrorTitle',
        description: message || 'inventory.apiErrors.getInventoryItemInfo',
      },
    });
  }

  private submitInventoryItem(item: CreateInventoryItemDto): void {
    this.isLoading.set(true);
    this.inventoryService
      .createInventoryItem(item)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap(() => {
          this.inventoryService.notifyRefresh();
          this.onClose();
        }),
        catchError((err) => {
          if (err.status === HttpStatusCode.CONFLICTS) {
            this.openErrorModal(err.error.message);
            return EMPTY;
          }

          this.openErrorModal();
          return EMPTY;
        }),
        finalize(() => this.isLoading.set(false)),
      )
      .subscribe();
  }

  private ensureBrandAndCategory(
    brandName: string,
    categoryName: string,
  ): Observable<{ brandId: number; categoryId: number }> {
    return forkJoin({
      brands: this.brandService.getBrands().pipe(take(1)),
      categories: this.categoryService.getCategories({}).pipe(take(1)),
    }).pipe(
      switchMap(({ brands, categories }) => {
        const foundBrand = brands.find(
          (brand) => brand.name.toLowerCase() === brandName.toLowerCase(),
        );

        const foundCategory = categories.data.categories.find(
          (category) => category.name.toLowerCase() === categoryName.toLowerCase(),
        );

        return forkJoin({
          brandId: foundBrand
            ? of(foundBrand.id)
            : this.brandService.createBrand(brandName).pipe(map((brand) => brand.id)),

          categoryId: foundCategory
            ? of(foundCategory.id)
            : this.categoryService
                .createCategory({ name: categoryName })
                .pipe(map((category) => category.id)),
        });
      }),
    );
  }
}
